/**
 * DataQualityDashboard Component
 * 
 * Comprehensive data quality dashboard with metrics, charts, and trend analysis.
 * Fully compliant with Section 508 requirements and WCAG 2.0 guidelines.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { DataAsset } from '../../types/DataAsset';

interface QualityMetric {
  id: string;
  name: string;
  value: number;
  target: number;
  trend: 'up' | 'down' | 'stable';
  status: 'excellent' | 'good' | 'warning' | 'critical';
  description: string;
}

interface QualityIssue {
  id: string;
  assetName: string;
  assetType: string;
  issueType: string;
  severity: 'high' | 'medium' | 'low';
  description: string;
  detectedAt: Date;
  assignedTo?: string;
  status: 'open' | 'in-progress' | 'resolved';
}

interface DataQualityDashboardProps {
  assets?: DataAsset[];
  timeRange?: string;
  onAssetClick?: (assetId: string) => void;
}

export const DataQualityDashboard: React.FC<DataQualityDashboardProps> = ({
  assets = [],
  timeRange = '30d',
  onAssetClick
}) => {
  const [loading, setLoading] = useState(true);
  const [selectedDomain, setSelectedDomain] = useState<string>('all');
  const [selectedMetric, setSelectedMetric] = useState<string>('all');
  const [qualityMetrics, setQualityMetrics] = useState<QualityMetric[]>([]);
  const [qualityIssues, setQualityIssues] = useState<QualityIssue[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);
  const [visibleTrends, setVisibleTrends] = useState<{
    overall: boolean;
    completeness: boolean;
    accuracy: boolean;
    consistency: boolean;
    timeliness: boolean;
  }>({
    overall: true,
    completeness: true,
    accuracy: true,
    consistency: true,
    timeliness: true
  });

  // Generate mock quality data
  useEffect(() => {
    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      // Generate quality metrics
      const metrics: QualityMetric[] = [
        {
          id: 'completeness',
          name: 'Data Completeness',
          value: 94.2,
          target: 95,
          trend: 'up',
          status: 'good',
          description: 'Percentage of non-null values across all fields'
        },
        {
          id: 'accuracy',
          name: 'Data Accuracy',
          value: 87.5,
          target: 90,
          status: 'warning',
          trend: 'down',
          description: 'Percentage of values that match expected patterns'
        },
        {
          id: 'consistency',
          name: 'Data Consistency',
          value: 91.8,
          target: 95,
          status: 'good',
          trend: 'stable',
          description: 'Consistency across related data sources'
        },
        {
          id: 'timeliness',
          name: 'Data Timeliness',
          value: 96.3,
          target: 95,
          status: 'excellent',
          trend: 'up',
          description: 'Percentage of data updated within SLA timeframes'
        },
        {
          id: 'validity',
          name: 'Data Validity',
          value: 83.1,
          target: 90,
          status: 'warning',
          trend: 'down',
          description: 'Percentage of values conforming to business rules'
        },
        {
          id: 'uniqueness',
          name: 'Data Uniqueness',
          value: 98.7,
          target: 98,
          status: 'excellent',
          trend: 'stable',
          description: 'Percentage of unique records without duplicates'
        }
      ];
      setQualityMetrics(metrics);

      // Generate quality issues
      const issues: QualityIssue[] = [
        {
          id: '1',
          assetName: 'Customer Master Table',
          assetType: 'Table',
          issueType: 'Missing Values',
          severity: 'high',
          description: 'Email field has 12% null values',
          detectedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
          assignedTo: 'Data Steward Team',
          status: 'in-progress'
        },
        {
          id: '2',
          assetName: 'Sales Transaction Data',
          assetType: 'Dataset',
          issueType: 'Data Format',
          severity: 'medium',
          description: 'Date format inconsistency detected',
          detectedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          assignedTo: 'John Smith',
          status: 'open'
        },
        {
          id: '3',
          assetName: 'Product Catalog',
          assetType: 'Table',
          issueType: 'Duplicate Records',
          severity: 'medium',
          description: '47 duplicate product entries found',
          detectedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
          status: 'resolved'
        },
        {
          id: '4',
          assetName: 'Financial Reports',
          assetType: 'Report',
          issueType: 'Stale Data',
          severity: 'high',
          description: 'Data not updated for 5 days',
          detectedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
          assignedTo: 'Finance Team',
          status: 'open'
        }
      ];
      setQualityIssues(issues);

      // Generate trend data with gradual improvement
      const trend = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        
        // Calculate improvement factor (0 at day 29, 1 at day 0)
        const improvementFactor = (29 - i) / 29;
        
        // Base values that improve over time
        const completenessBase = 88 + (improvementFactor * 6); // 88% -> 94%
        const accuracyBase = 85 + (improvementFactor * 5); // 85% -> 90%
        const consistencyBase = 87 + (improvementFactor * 5); // 87% -> 92%
        const timelinessBase = 92 + (improvementFactor * 4); // 92% -> 96%
        
        // Add small random variation (±1.5%) to make it look realistic
        const randomVariation = () => (Math.random() - 0.5) * 3;
        
        const completeness = Math.min(98, Math.max(85, completenessBase + randomVariation()));
        const accuracy = Math.min(95, Math.max(82, accuracyBase + randomVariation()));
        const consistency = Math.min(96, Math.max(85, consistencyBase + randomVariation()));
        const timeliness = Math.min(99, Math.max(90, timelinessBase + randomVariation()));
        
        // Overall is weighted average
        const overall = (completeness + accuracy + consistency + timeliness) / 4;
        
        trend.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          completeness: Math.round(completeness * 10) / 10,
          accuracy: Math.round(accuracy * 10) / 10,
          consistency: Math.round(consistency * 10) / 10,
          timeliness: Math.round(timeliness * 10) / 10,
          overall: Math.round(overall * 10) / 10
        });
      }
      setTrendData(trend);
      
      setLoading(false);
    }, 1000);
  }, [timeRange, selectedDomain]);

  // Get status color
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'excellent': return '#4CAF50';
      case 'good': return '#8BC34A';
      case 'warning': return '#FF9800';
      case 'critical': return '#F44336';
      default: return '#757575';
    }
  };

  // Get severity color
  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'high': return '#F44336';
      case 'medium': return '#FF9800';
      case 'low': return '#4CAF50';
      default: return '#757575';
    }
  };

  // Calculate overall quality score
  const overallQualityScore = useMemo(() => {
    if (qualityMetrics.length === 0) return 0;
    return Math.round(qualityMetrics.reduce((sum, metric) => sum + metric.value, 0) / qualityMetrics.length);
  }, [qualityMetrics]);

  // Filter issues by severity
  const issuesBySeverity = useMemo(() => {
    return {
      high: qualityIssues.filter(issue => issue.severity === 'high').length,
      medium: qualityIssues.filter(issue => issue.severity === 'medium').length,
      low: qualityIssues.filter(issue => issue.severity === 'low').length
    };
  }, [qualityIssues]);

  // Pie chart data for issues
  const issuesPieData = [
    { name: 'High', value: issuesBySeverity.high, color: '#F44336' },
    { name: 'Medium', value: issuesBySeverity.medium, color: '#FF9800' },
    { name: 'Low', value: issuesBySeverity.low, color: '#4CAF50' }
  ].filter(item => item.value > 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading quality metrics...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ color: '#003366', fontWeight: 700 }}>
          Data Quality Dashboard
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Domain</InputLabel>
            <Select
              value={selectedDomain}
              onChange={(e) => setSelectedDomain(e.target.value)}
              label="Domain"
            >
              <MenuItem value="all">All Domains</MenuItem>
              <MenuItem value="finance">Finance</MenuItem>
              <MenuItem value="hr">Human Resources</MenuItem>
              <MenuItem value="sales">Sales</MenuItem>
              <MenuItem value="operations">Operations</MenuItem>
            </Select>
          </FormControl>
          
          <Tooltip title="Refresh Data">
            <IconButton onClick={() => window.location.reload()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Export Report">
            <IconButton>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Overall Score Card */}
      <Card sx={{ mb: 3, bgcolor: '#f8f9fa', border: '2px solid #e9ecef' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Overall Data Quality Score
              </Typography>
              <Typography variant="h2" sx={{ color: getStatusColor(overallQualityScore >= 95 ? 'excellent' : overallQualityScore >= 85 ? 'good' : overallQualityScore >= 70 ? 'warning' : 'critical'), fontWeight: 'bold' }}>
                {overallQualityScore}%
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Chip
                label={overallQualityScore >= 95 ? 'Excellent' : overallQualityScore >= 85 ? 'Good' : overallQualityScore >= 70 ? 'Needs Attention' : 'Critical'}
                color={overallQualityScore >= 85 ? 'success' : overallQualityScore >= 70 ? 'warning' : 'error'}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                Based on {qualityMetrics.length} key metrics
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Quality Metrics Grid */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {qualityMetrics.map((metric) => (
          <Grid item xs={12} sm={6} md={4} key={metric.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="h3" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    {metric.name}
                  </Typography>
                  {metric.trend === 'up' && <TrendingUpIcon color="success" />}
                  {metric.trend === 'down' && <TrendingDownIcon color="error" />}
                  {metric.trend === 'stable' && <CheckCircleIcon color="action" />}
                </Box>
                
                <Typography variant="h4" sx={{ color: getStatusColor(metric.status), fontWeight: 'bold', mb: 1 }}>
                  {metric.value}%
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    Target: {metric.target}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(metric.value / metric.target) * 100}
                    sx={{
                      flex: 1,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'divider',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: getStatusColor(metric.status)
                      }
                    }}
                  />
                </Box>
                
                <Typography variant="caption" color="text.secondary">
                  {metric.description}
                </Typography>
                
                <Chip
                  label={metric.status.charAt(0).toUpperCase() + metric.status.slice(1)}
                  size="small"
                  sx={{
                    mt: 1,
                    bgcolor: getStatusColor(metric.status),
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Trend Chart */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Quality Trends (Last 30 Days)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label="Overall"
                  onClick={() => setVisibleTrends(prev => ({ ...prev, overall: !prev.overall }))}
                  color={visibleTrends.overall ? 'primary' : 'default'}
                  variant={visibleTrends.overall ? 'filled' : 'outlined'}
                  size="small"
                  sx={{ 
                    bgcolor: visibleTrends.overall ? '#003366' : 'transparent',
                    '&:hover': { bgcolor: visibleTrends.overall ? '#002244' : '#f5f5f5' }
                  }}
                />
                <Chip
                  label="Completeness"
                  onClick={() => setVisibleTrends(prev => ({ ...prev, completeness: !prev.completeness }))}
                  color={visibleTrends.completeness ? 'info' : 'default'}
                  variant={visibleTrends.completeness ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  label="Accuracy"
                  onClick={() => setVisibleTrends(prev => ({ ...prev, accuracy: !prev.accuracy }))}
                  color={visibleTrends.accuracy ? 'success' : 'default'}
                  variant={visibleTrends.accuracy ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  label="Consistency"
                  onClick={() => setVisibleTrends(prev => ({ ...prev, consistency: !prev.consistency }))}
                  color={visibleTrends.consistency ? 'warning' : 'default'}
                  variant={visibleTrends.consistency ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  label="Timeliness"
                  onClick={() => setVisibleTrends(prev => ({ ...prev, timeliness: !prev.timeliness }))}
                  sx={{
                    bgcolor: visibleTrends.timeliness ? '#9C27B0' : 'transparent',
                    color: visibleTrends.timeliness ? 'white' : 'inherit',
                    borderColor: visibleTrends.timeliness ? '#9C27B0' : 'rgba(0, 0, 0, 0.23)',
                    '&:hover': { bgcolor: visibleTrends.timeliness ? '#7B1FA2' : '#f5f5f5' }
                  }}
                  variant={visibleTrends.timeliness ? 'filled' : 'outlined'}
                  size="small"
                />
              </Box>
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis domain={[80, 100]} />
                <RechartsTooltip />
                <Legend />
                {visibleTrends.overall && (
                  <Line type="monotone" dataKey="overall" stroke="#003366" strokeWidth={3} name="Overall Quality" />
                )}
                {visibleTrends.completeness && (
                  <Line type="monotone" dataKey="completeness" stroke="#2196F3" name="Completeness" />
                )}
                {visibleTrends.accuracy && (
                  <Line type="monotone" dataKey="accuracy" stroke="#4CAF50" name="Accuracy" />
                )}
                {visibleTrends.consistency && (
                  <Line type="monotone" dataKey="consistency" stroke="#FF9800" name="Consistency" />
                )}
                {visibleTrends.timeliness && (
                  <Line type="monotone" dataKey="timeliness" stroke="#9C27B0" name="Timeliness" />
                )}
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Issues Pie Chart */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quality Issues by Severity
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={issuesPieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {issuesPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Total Issues: {qualityIssues.length}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Issues Table */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Recent Quality Issues
          </Typography>
          <Button variant="outlined" size="small" startIcon={<FilterIcon />}>
            Filter Issues
          </Button>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Asset</TableCell>
                <TableCell>Issue Type</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Detected</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {qualityIssues.map((issue) => (
                <TableRow key={issue.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {issue.assetName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {issue.assetType}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{issue.issueType}</TableCell>
                  <TableCell>
                    <Chip
                      label={issue.severity.toUpperCase()}
                      size="small"
                      sx={{
                        bgcolor: getSeverityColor(issue.severity),
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                  </TableCell>
                  <TableCell>{issue.description}</TableCell>
                  <TableCell>
                    {issue.detectedAt.toLocaleDateString()}
                  </TableCell>
                  <TableCell>{issue.assignedTo || 'Unassigned'}</TableCell>
                  <TableCell>
                    <Chip
                      label={issue.status.replace('-', ' ').toUpperCase()}
                      size="small"
                      color={issue.status === 'resolved' ? 'success' : issue.status === 'in-progress' ? 'warning' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default DataQualityDashboard;
